import { FC, MouseEvent, useState } from 'react';
import { Popover, Tooltip } from '@mui/material';
import styled from 'styled-components';
import { editorTheme } from '../../theme';
import ColorPicker from '../Widgets/ColorPicker';
import { ToolButton } from './ToolbarControls';
import { sharedValue, useSelectedText } from './useSelectedText';

const Glyph = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
`;

// Shows every color when the selected texts differ.
const Bar = styled.span<{ swatch: string | null }>`
  width: 16px;
  height: 4px;
  border-radius: 1px;
  border: 1px solid ${editorTheme.border};
  background: ${({ swatch }) =>
    swatch ||
    'linear-gradient(90deg, #ff3131, #ffde59, #00bf63, #38b6ff, #8c52ff)'};
`;

const Panel = styled.div`
  width: 260px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TextColor: FC = () => {
  const { objects } = useSelectedText();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const fill = sharedValue(objects, (object) =>
    typeof object.fill === 'string' ? object.fill : null,
  );

  return (
    <>
      <Tooltip title="Text color">
        <ToolButton
          aria-label="Text color"
          aria-haspopup="dialog"
          aria-expanded={!!anchor}
          active={!!anchor}
          onClick={(event: MouseEvent<HTMLButtonElement>) =>
            setAnchor(event.currentTarget)
          }
        >
          <Glyph aria-hidden="true">
            A
            <Bar swatch={fill} />
          </Glyph>
        </ToolButton>
      </Tooltip>
      <Popover
        open={!!anchor && !!objects.length}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ role: 'dialog', 'aria-label': 'Text color' }}
      >
        <Panel>
          <ColorPicker textOnly />
        </Panel>
      </Popover>
    </>
  );
};

export default TextColor;
